import "@fontsource-variable/geist-mono/wght.css";
import "@fontsource-variable/inter/wght.css";
import "@fontsource-variable/space-grotesk/wght.css";
import { SnackbarProvider } from "notistack";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import React, { Suspense } from "react";
import { Provider } from "react-redux";

import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { Toaster } from "@pomerium/ui/components/ui/sonner";

import "./App.css";
import LoadingScreen from "./components/LoadingScreen";
import PricingInfo from "./components/PricingInfo";
import { firebaseAuth } from "./configs/firebase";
import { setSentryUser } from "./configs/sentry";
import { UserProvider } from "./contexts/UserContext";
import {
  SettingsConsumer,
  SettingsProvider,
} from "./contexts/settings-context";
import { store } from "./store";
import { createTheme } from "./theme";
import { QueryClientProvider } from "./utils/connect";
import Router from "./views/router";

declare global {
  interface signalArgs {
    email: string;
    name: string;
  }
  interface Window {
    syft: {
      signup: (email: string, fields: Record<string, string>) => void;
      identify: (email: string, fields: Record<string, string>) => void;
    };
    signals: {
      identify: ({ email, name }: signalArgs) => void;
    };
  }

  // see https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/
  const $crisp: {
    push: (
      command:
        | ["do", string]
        | ["set", string, any]
        | ["on", string, (...args: any[]) => void],
    ) => void;
  };
}

// Initialize PostHog once
if (
  typeof window !== "undefined" &&
  !posthog.__loaded &&
  import.meta.env.VITE_PUBLIC_POSTHOG_KEY
) {
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST,
    capture_pageview: false,
  });
}

function App() {
  // Set up Firebase auth state listener on mount
  React.useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((u: any) => {
      setSentryUser(
        u
          ? {
              id: u.uid,
              email: u.email || undefined,
            }
          : null,
      );
    });

    return () => unsubscribe();
  }, []);
  return (
    <PostHogProvider client={posthog}>
      <Provider store={store}>
        <QueryClientProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <UserProvider>
              <SettingsProvider>
                <SettingsConsumer>
                  {(settings) => {
                    // Prevent theme flicker when restoring custom settings from browser storage
                    if (!settings.isInitialized) {
                      // return null;
                    }

                    const theme = createTheme({
                      direction: "ltr",
                      paletteMode: settings.paletteMode,
                    });

                    const root = window.document.documentElement;
                    if (settings.paletteMode) {
                      root.classList.add(settings.paletteMode || "light");
                      if (settings.paletteMode === "light") {
                        root.classList.remove("dark");
                      } else {
                        root.classList.remove("light");
                      }
                    }

                    return (
                      <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <SnackbarProvider
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                          maxSnack={3}
                          autoHideDuration={2000}
                          preventDuplicate={true}
                        >
                          <Suspense fallback={<LoadingScreen />}>
                            <PricingInfo />
                            <Router />
                            <Toaster />
                          </Suspense>
                        </SnackbarProvider>
                      </ThemeProvider>
                    );
                  }}
                </SettingsConsumer>
              </SettingsProvider>
            </UserProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </Provider>
    </PostHogProvider>
  );
}

export default App;
